import React, { useState, useEffect, useRef } from"react";
import { useLocation, useNavigate } from"react-router-dom";
import useSkillGenieSession from"../hooks/useSkillGenieSession";
import API_BASE_URL from"../Config/config";
import FeatureGuard from"./access/FeatureGuard";
import TutorShell, { RailSection, RailStat, RailTip } from "./aiTutor/TutorShell";
import { useTutorLanguage, LanguagePicker } from "./aiTutor/languages";
import {
  TutorMessages,
  TutorComposer,
  TutorBadge,
  TutorEmptyState,
} from "./aiTutor/TutorChat";
import NexusUsageBadge from "../NextGenBuilder/components/NexusUsageBadge";
import useNexusLearnerBilling from "../NextGenBuilder/hooks/useNexusLearnerBilling";

const SkillTestPage = ({ mode = "learner" }) => {
 const isTenantMode = mode === "tenant";

 // Tenant / NextGen Builder mode bypasses the learner paywall — access is
 // already gated externally (NextGenPaidFeatureGuard for TSG Nexus).
 if (isTenantMode) {
   return <SkillTestPageContent mode={mode} />;
 }

 return (
  <FeatureGuard feature="TSG_AI">
    <SkillTestPageContent mode={mode} />
  </FeatureGuard>
 );
};

const SkillTestPageContent = ({ mode = "learner" }) => {
 const isTenantMode = mode === "tenant";
 const [input, setInput] = useState("");
 const [chat, setChat] = useState([]);
 const [loading, setLoading] = useState(false);
 const chatEndRef = useRef(null);
 const navigate = useNavigate();

 // 🔹 Get language from navigation state or stored session
 const location = useLocation();
 const { session } = useSkillGenieSession();
  const { langCode, setLanguage } = useTutorLanguage({
    initialLanguage: location.state?.language || session?.language,
  });
  const selectedLanguage = langCode;

 // TSG Nexus feature billing — inert outside /nextgen-builder/*.
 const nexus = useNexusLearnerBilling("AI_TUTOR", { subFeature: "SKILL_TEST" });

 const handleAsk = async () => {
 if (!input.trim()) return;
 const currentInput = input;
 setChat((prev) => [...prev, { role: "user", content: currentInput }]);
 setInput("");
 setLoading(true);
 const user = JSON.parse(localStorage.getItem("user") || "{}");
 // NextGen Builder accounts don't have `.userId`/`.user_id` — fall back to
 // the namespaced pseudo-identity written to localStorage by
 // NextGenAuthContext.jsx, same as ExplainPage.jsx already does.
 const userId = user?.userId || user?.user_id || localStorage.getItem("user_id") || "guest";

 try {
 const res = await fetch(`${API_BASE_URL}/tsg/skill_test`, {
 method:"POST",
 headers: {"Content-Type":"application/json", ...nexus.headers() },
 body: JSON.stringify({
 userId,
 prompt: currentInput,
 chatHistory: chat,
 language: selectedLanguage, // ✅ dynamic language
 technology:"general",
 topic:"general",
 }),
 });

 const data = await res.json();

 if (res.status === 402 && nexus.handleQuota(data)) {
 setChat((prev) => [
 ...prev,
 { role:"assistant", content:"You've used all your free Skill Test prompts for this month. Recharge to continue." },
 ]);
 return;
 }

 nexus.noteUsage(res);

 const reply = data.reply ||"No reply.";
 setChat((prev) => [
 ...prev,
 { role:"assistant", content: reply },
 ]);
 } catch {
 setChat((prev) => [
 ...prev,
 { role:"assistant", content:"❌ Error reaching TSG." },
 ]);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
    const container = chatEndRef.current?.parentElement;
    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 100;
      if (isAtBottom || chat.length <= 2) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [chat]);

  const STARTERS = [
    "Test me on JavaScript fundamentals",
    "10 questions on SQL joins and indexes",
    "Quiz me on React hooks, increasing difficulty",
    "Assess my understanding of REST API design",
  ];

  return (
    <TutorShell
      language={<LanguagePicker value={langCode} onChange={setLanguage} />}
      icon={<svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>}
      title="Skill Test"
      subtitle="Generate an assessment on any topic and work through it question by question."
      onBack={() => navigate(-1)}
      headerExtra={nexus.isNexus ? <NexusUsageBadge feature="AI_TUTOR" subFeature="SKILL_TEST" /> : null}
      badges={<TutorBadge label="Lang" value={selectedLanguage} dot="#22C55E" />}
      rail={
        <>
          <RailSection title="Progress">
            <RailStat label="Exchanges" value={String(Math.ceil(chat.length / 2))} hint="Each reply keeps the full test as context." />
            <RailStat label="Language" value={selectedLanguage} />
          </RailSection>
          <RailSection title="Test yourself on">
            {STARTERS.map((s) => (
              <button
                key={s}
                onClick={() => setInput(s)}
                className="w-full text-left rounded-2xl border border-[#E9E7F3] bg-white px-4 py-3 mb-2 last:mb-0 hover:border-[#5B21B6] hover:bg-[#F5F2FF] transition-all"
              >
                <span className="text-[12px] font-semibold text-[#4B3F72] leading-snug">{s}</span>
              </button>
            ))}
          </RailSection>
          <RailSection title="Tips">
            <RailTip>Say how many questions you want and at what level — the test is built to match.</RailTip>
            <RailTip>Answer first, then ask for the mark scheme. Grading your own attempt sticks better.</RailTip>
          </RailSection>
        </>
      }
      composer={
        <TutorComposer
          value={input}
          onChange={setInput}
          onSubmit={handleAsk}
          loading={loading}
          disabled={!input.trim()}
          placeholder="Topic to test your skills..."
          hint={nexus.hint}
        />
      }
    >
      <TutorMessages
        chat={chat}
        chatEndRef={chatEndRef}
        empty={
          <TutorEmptyState
            icon={<svg className="w-9 h-9 text-[#5B21B6]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>}
            title="What should we test?"
            description="Name a topic and I will build an assessment, then mark your answers and explain what you missed."
          >
            <div className="grid sm:grid-cols-2 gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  className="text-left rounded-2xl border border-[#E9E7F3] bg-white px-4 py-3 hover:border-[#5B21B6] hover:bg-[#F5F2FF] transition-all"
                >
                  <span className="text-[13px] font-semibold text-[#4B3F72] leading-snug">{s}</span>
                </button>
              ))}
            </div>
          </TutorEmptyState>
        }
      />
    </TutorShell>
  );
};

export default SkillTestPage;



